import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { formatAgentOptions } from '../../../api/agents/formatters'
import agents from '../../../api/agents/hooks'
import { contactDetailSearchFieldsConfig, contactProfileDetailSearchFieldsConfig } from '../../../api/contacts/formValuesConfig'
import contacts from '../../../api/contacts/hooks'
import { documentsDetailSearchFieldsConfig } from '../../../api/documents/formValuesConfig'
import documents from '../../../api/documents/hooks'
import { formatExportServiceOptions } from '../../../api/export/formatters'
import exportApi from '../../../api/export/hooks'
import { locationContactProfileDetailSearchFieldsConfig, locationPropertyDetailSearchFieldsConfig } from '../../../api/locations/formValuesConfig'
import { projectDetailSearchFieldsConfig } from '../../../api/projects/formValuesConfig'
import projects from '../../../api/projects/hooks'
import { propertyDetailSearchFieldsConfig } from '../../../api/properties/formValuesConfig'
import properties from '../../../api/properties/hooks'
import { DETAIL_SEARCH_TYPE, WEBSITE_ATTRIBUTES } from '../../../common/constants'
import { contactDetailSearchFields } from '../../../common/forms/contacts/fields'
import { propertyDetailSearchFields } from '../../../common/forms/generalRealEstate/fields'
import { projectDetailSearchFields } from '../../../common/forms/projects/fields'
import { useLocationValues } from '../../../hooks/useLocationValues'
import { selectContactProfilePropertyTypeFilter, selectPropertyTypeFilter } from '../../../store/formFilters'
import { enableFormValues, mapValuesToFormFields } from '../../../utils/formUtils/forms'

export const useDetailSearchFormValues = (type, t) => {
    const { propertyTypeFilter, contactPropertyTypeFilter } = useSelector((state) => ({
        propertyTypeFilter: selectPropertyTypeFilter(state),
        contactPropertyTypeFilter: selectContactProfilePropertyTypeFilter(state),
    }))

    const formValuesQueries = {
        projects: {
            fields: ['status', 'type', 'city'],
            enabled: enableFormValues([DETAIL_SEARCH_TYPE.PROJECT], type),
        },
        properties: {
            fields: {
                static: [
                    'propertyService',
                    'propertyType',
                    'status',
                    'energyEfficiency',
                    'condition',
                    'dataSource',
                    'descriptions',
                    'utility',
                    'space',
                    'equipment',
                ],
                dynamic: ['propertySubtype', 'floor'],
            },
            enabled: {
                static: enableFormValues([DETAIL_SEARCH_TYPE.PROPERTY], type),
                dynamic: enableFormValues([DETAIL_SEARCH_TYPE.PROPERTY], type, propertyTypeFilter),
            },
        },
        contacts: {
            fields: {
                static: [
                    'country',
                    'queryCategory',
                    'priority',
                    'status',
                    'profileStatus',
                    'profileService',
                    'profileDuration',
                    'propertyType',
                    'dataSource',
                ],
                dynamic: ['propertySubtype', 'floor'],
            },
            enabled: {
                static: enableFormValues([DETAIL_SEARCH_TYPE.CONTACT, DETAIL_SEARCH_TYPE.COMPANY], type),
                dynamic: enableFormValues([DETAIL_SEARCH_TYPE.CONTACT, DETAIL_SEARCH_TYPE.COMPANY], type, contactPropertyTypeFilter),
            },
        },
        documents: {
            fields: ['type'],
            enabled: enableFormValues([DETAIL_SEARCH_TYPE.DOCUMENT], type),
        },
    }

    const propertyFilters = useMemo(() => {
        if (!propertyTypeFilter) return undefined

        if (Array.isArray(propertyTypeFilter) && propertyTypeFilter.length === 0) return undefined

        return { propertyType: propertyTypeFilter }
    }, [propertyTypeFilter])

    const contactPropertyFilters = useMemo(() => {
        if (!contactPropertyTypeFilter) return undefined

        if (Array.isArray(contactPropertyTypeFilter) && contactPropertyTypeFilter.length === 0) return undefined

        return { propertyType: contactPropertyTypeFilter }
    }, [contactPropertyTypeFilter])

    const { data: projectValues, isPending: isProjectValuesPending } = projects.FormValues.useQuery(
        formValuesQueries.projects.fields,
        formValuesQueries.projects.enabled
    )
    const { data: staticPropertyValues, isPending: isStaticPropertyValuesPending } = properties.FormValues.useQuery(
        formValuesQueries.properties.fields.static,
        undefined,
        formValuesQueries.properties.enabled.static
    )
    const { data: dynamicPropertyValues, isPending: isDynamicPropertyValuesPending } = properties.FormValues.useQuery(
        formValuesQueries.properties.fields.dynamic,
        propertyFilters,
        formValuesQueries.properties.enabled.dynamic
    )
    const { data: staticContactValues, isPending: isStaticContactValuesPending } = contacts.FormValues.useQuery(
        formValuesQueries.contacts.fields.static,
        undefined,
        formValuesQueries.contacts.enabled.static
    )
    const { data: dynamicContactValues, isPending: isDynamicContactValuesPending } = contacts.FormValues.useQuery(
        formValuesQueries.contacts.fields.dynamic,
        contactPropertyFilters,
        formValuesQueries.contacts.enabled.dynamic
    )
    const { data: documentValues, isPending: isDocumentValuesPending } = documents.FormValues.useQuery(
        formValuesQueries.documents.fields,
        formValuesQueries.documents.enabled
    )

    const { data: agentsData, isPending: isAgentsPending } = agents.GetAgents.useQuery()

    const { data: exportPortalsData, isPending: isExportPortalsPending } = exportApi.GetPortals.useQuery()
    const { data: exportWebsitesData, isPending: isExportWebsitesPending } = exportApi.GetWebsites.useQuery()

    const { locationValues, isPending: isLocationValuesPending } = useLocationValues(type)

    const propertyValues = useMemo(
        () => ({
            ...staticPropertyValues,
            ...dynamicPropertyValues,
        }),
        [staticPropertyValues, dynamicPropertyValues]
    )

    const contactValues = useMemo(
        () => ({
            ...staticContactValues,
            ...dynamicContactValues,
        }),
        [staticContactValues, dynamicContactValues]
    )

    const exportPortals = useMemo(() => formatExportServiceOptions(exportPortalsData), [exportPortalsData])
    const exportWebsites = useMemo(() => formatExportServiceOptions(exportWebsitesData), [exportWebsitesData])

    const agentOptions = formatAgentOptions(agentsData)

    const mappingConfigs = useMemo(
        () => ({
            [DETAIL_SEARCH_TYPE.DOCUMENT]: {
                selectOptions: [{ data: documentValues, config: documentsDetailSearchFieldsConfig }],
                selectorValues: {},
            },
            [DETAIL_SEARCH_TYPE.PROJECT]: {
                selectOptions: [
                    { data: projectValues, config: projectDetailSearchFieldsConfig },
                    { data: locationValues, config: locationPropertyDetailSearchFieldsConfig },
                ],
                selectorValues: {
                    [projectDetailSearchFields.AGENT]: agentOptions,
                },
            },
            [DETAIL_SEARCH_TYPE.PROPERTY]: {
                selectOptions: [
                    { data: propertyValues, config: propertyDetailSearchFieldsConfig },
                    { data: locationValues, config: locationPropertyDetailSearchFieldsConfig },
                ],
                selectorValues: {
                    [propertyDetailSearchFields.PRIMARY_AGENT]: agentOptions,
                    [propertyDetailSearchFields.WEB]: exportWebsites,
                    [propertyDetailSearchFields.PORTAL]: exportPortals,
                    [propertyDetailSearchFields.ATTRIBUTE]: WEBSITE_ATTRIBUTES?.map((item) => ({
                        value: item.attribute,
                        label: t(item.name),
                    })),
                },
            },
            [DETAIL_SEARCH_TYPE.CONTACT]: {
                selectOptions: [{ data: contactValues, config: contactDetailSearchFieldsConfig }],
                selectorValues: {
                    [contactDetailSearchFields.AGENT]: agentOptions,
                },
                profileSelectOptions: [
                    { data: contactValues, config: contactProfileDetailSearchFieldsConfig },
                    { data: locationValues, config: locationContactProfileDetailSearchFieldsConfig },
                ],
            },
            [DETAIL_SEARCH_TYPE.COMPANY]: {
                selectOptions: [{ data: contactValues, config: contactDetailSearchFieldsConfig }],
                selectorValues: {
                    [contactDetailSearchFields.AGENT]: agentOptions,
                },
                profileSelectOptions: [
                    { data: contactValues, config: contactProfileDetailSearchFieldsConfig },
                    { data: locationValues, config: locationContactProfileDetailSearchFieldsConfig },
                ],
            },
        }),
        [projectValues, propertyValues, contactValues, documentValues, locationValues, exportWebsites, exportPortals, agentOptions, t]
    )

    const selectOptions = useMemo(() => {
        const config = mappingConfigs[type]
        if (!config) return {}

        const mappedValues = config.selectOptions.reduce((acc, { data, config: fieldConfig }) => {
            return {
                ...acc,
                ...mapValuesToFormFields(data, fieldConfig),
            }
        }, {})

        return {
            ...mappedValues,
            ...(config.selectorValues || {}),
        }
    }, [type, mappingConfigs])

    const profileSelectOptions = useMemo(() => {
        const config = mappingConfigs[type]

        if (!config) return {}

        return (config.profileSelectOptions ?? []).reduce((acc, { data, config: fieldConfig }) => {
            return {
                ...acc,
                ...mapValuesToFormFields(data, fieldConfig),
            }
        }, {})
    }, [type, mappingConfigs])

    // a disabled query (enabled: false) reports isPending: true forever, so only count it while it's actually enabled
    const isPending =
        (formValuesQueries.projects.enabled.enabled && isProjectValuesPending) ||
        (formValuesQueries.properties.enabled.static.enabled && isStaticPropertyValuesPending) ||
        (formValuesQueries.properties.enabled.dynamic.enabled && isDynamicPropertyValuesPending) ||
        (formValuesQueries.contacts.enabled.static.enabled && isStaticContactValuesPending) ||
        (formValuesQueries.contacts.enabled.dynamic.enabled && isDynamicContactValuesPending) ||
        (formValuesQueries.documents.enabled.enabled && isDocumentValuesPending) ||
        isAgentsPending ||
        isExportPortalsPending ||
        isExportWebsitesPending ||
        isLocationValuesPending

    return {
        selectOptions,
        profileSelectOptions,
        isPending,
    }
}
